import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import axios from '../config/axios'
import UserContext, { Roles } from './UserContext'

export interface ColumnsPreference {
  order: string[]
  hidden: string[]
}

interface UserPreferencesContextType {
  preferences: Record<string, any>
  getPreference: <T>(key: string, defaultValue?: T) => T
  getEffectivePreference: <T>(key: string, defaultValue?: T) => T
  setPreference: <T>(key: string, value: T) => Promise<void>
  getColumnsPreference: (screenKey: string, defaults: ColumnsPreference) => ColumnsPreference
  saveColumnsPreference: (screenKey: string, value: ColumnsPreference) => Promise<void>
  resetColumnsPreference: (screenKey: string) => Promise<void>
  isLoading: boolean
  reloadPreferences: () => Promise<void>
  isTenantAdmin: boolean
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined)

export const UserPreferencesProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { user } = useContext(UserContext)

  const isTenantAdmin = (user?.role as any) === 'TENANT_ADMIN' || (user?.role as any) === Roles.tenantAdmin

  const reloadPreferences = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      const response = await axios.get('/pipelines/user-preference')
      if (response.data) {
        setPreferences(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar preferências do usuário:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    reloadPreferences()
  }, [reloadPreferences])

  const getPreference = useCallback(<T,>(key: string, defaultValue?: T): T => {
    const val = preferences[key]
    if (val === undefined) {
      return defaultValue as T
    }
    return val as T
  }, [preferences])

  const getEffectivePreference = useCallback(<T,>(key: string, defaultValue?: T): T => {
    if (isTenantAdmin) {
      return getPreference<T>(key, defaultValue)
    }

    const userValue = getPreference<T | undefined>(key, undefined)
    if (userValue !== undefined && userValue !== null) {
      return userValue
    }

    return getPreference<T>(`admin_${key}`, defaultValue as T)
  }, [getPreference, isTenantAdmin])

  const setPreference = useCallback(async <T,>(key: string, value: T) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }))

    try {
      await axios.post('/pipelines/user-preference', { key, value })
    } catch (error) {
      console.error(`Erro ao salvar preferência ${key} no banco:`, error)
    }
  }, [])

  const getColumnsPreference = useCallback((screenKey: string, defaults: ColumnsPreference): ColumnsPreference => {
    const userKey = `columns_${screenKey}`
    const legacyAdminKey = `admin_columns_${screenKey}`
    const userPref = getPreference<ColumnsPreference | undefined>(userKey, undefined)

    if (isTenantAdmin) {
      return userPref ?? getPreference<ColumnsPreference | undefined>(legacyAdminKey, undefined) ?? defaults
    }

    const adminPref =
      getPreference<ColumnsPreference | undefined>(`admin_${userKey}`, undefined) ??
      getPreference<ColumnsPreference | undefined>(`admin_${legacyAdminKey}`, undefined)
    return userPref ?? adminPref ?? defaults
  }, [getPreference, isTenantAdmin])

  const saveColumnsPreference = useCallback(async (screenKey: string, value: ColumnsPreference) => {
    await setPreference(`columns_${screenKey}`, value)
  }, [setPreference])

  const resetColumnsPreference = useCallback(async (screenKey: string) => {
    await setPreference(`columns_${screenKey}`, null)
    if (isTenantAdmin && getPreference(`admin_columns_${screenKey}`, undefined) != null) {
      await setPreference(`admin_columns_${screenKey}`, null)
    }
  }, [setPreference, isTenantAdmin, getPreference])

  return (
    <UserPreferencesContext.Provider value={{ preferences, getPreference, getEffectivePreference, setPreference, getColumnsPreference, saveColumnsPreference, resetColumnsPreference, isLoading, reloadPreferences, isTenantAdmin }}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext)
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider')
  }
  return context
}
